import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  Fab,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  fetchWizzardStatus,
  wizzardChat,
  type WizzardChatMessage,
} from '../../services/wizzardApi';

const QUICK_PROMPTS = [
  'How much revenue did we collect in the last 30 days?',
  'Which invoices are overdue?',
  'Help me write a friendly payment reminder',
  'What should I say to a customer 30 days overdue?',
];

const CUSTOMER_REPLY_PREFILL =
  'Draft a reply to this customer email. Look up matching Xero contacts, summarise the last 12 months as a statement for each site/account mentioned, explain any overlapping trading names or invoice references, and cover changing the invoice email if they asked.\n\n---\n';

export function WizzardCopilot() {
  const [open, setOpen] = useState(false);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [messages, setMessages] = useState<WizzardChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      return;
    }
    void fetchWizzardStatus()
      .then((s) => setConfigured(s.configured && s.enabled))
      .catch(() => setConfigured(false));
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || loading) {
        return;
      }
      setError(null);
      const next: WizzardChatMessage[] = [...messages, { role: 'user', content: trimmed }];
      setMessages(next);
      setInput('');
      setLoading(true);
      try {
        const pageContext = `Route: ${location.pathname}`;
        const reply = await wizzardChat(next, pageContext);
        setMessages([...next, { role: 'assistant', content: reply }]);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Wizzard could not respond');
      } finally {
        setLoading(false);
      }
    },
    [loading, location.pathname, messages]
  );

  const startCustomerReply = useCallback(() => {
    setInput(CUSTOMER_REPLY_PREFILL);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  return (
    <>
      <Fab
        color="secondary"
        aria-label="Open Wizzard assistant"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1200,
          background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
          '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #1d4ed8 100%)' },
        }}
      >
        <AutoFixHighIcon />
      </Fab>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}>
        <Stack sx={{ height: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 2,
              py: 1.5,
              background: 'linear-gradient(135deg, #0f172a 0%, #4c1d95 100%)',
              color: '#fff',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoFixHighIcon />
              <Box>
                <Typography fontWeight={800}>Wizzard</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  Grok copilot · reminders, AR & customer replies
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={() => setOpen(false)} sx={{ color: '#fff' }} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          {configured === false && (
            <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
              Add your{' '}
              <Button size="small" onClick={() => navigate('/settings#wizzard')}>
                Grok API key
              </Button>{' '}
              in API Settings to enable Wizzard.
            </Alert>
          )}

          <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {messages.length === 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Ask about revenue, overdue invoices, draft reminders, or paste a customer email for a reply with Xero statement context.
                </Typography>
                <Stack spacing={1} mt={2}>
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                    onClick={startCustomerReply}
                    disabled={configured === false}
                  >
                    Draft a reply to a customer email…
                  </Button>
                  {QUICK_PROMPTS.map((q) => (
                    <Button key={q} size="small" variant="outlined" sx={{ justifyContent: 'flex-start', textAlign: 'left' }} onClick={() => void send(q)}>
                      {q}
                    </Button>
                  ))}
                </Stack>
              </Box>
            )}
            {messages.map((m, i) => (
              <Box
                key={i}
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: m.role === 'user' ? 'primary.main' : 'action.hover',
                  color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  ml: m.role === 'user' ? 4 : 0,
                  mr: m.role === 'assistant' ? 4 : 0,
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {m.content}
                </Typography>
              </Box>
            ))}
            {loading && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="caption" color="text.secondary">
                  Wizzard is thinking…
                </Typography>
              </Stack>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}
          </Box>

          <Stack direction="row" spacing={1} sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Ask Wizzard… or paste a customer email"
              value={input}
              inputRef={inputRef}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                const submitCombo = e.key === 'Enter' && (e.metaKey || e.ctrlKey);
                const submitSingleLine = e.key === 'Enter' && !e.shiftKey && !input.includes('\n');
                if (submitCombo || submitSingleLine) {
                  e.preventDefault();
                  void send(input);
                }
              }}
              disabled={loading || configured === false}
              multiline
              maxRows={8}
            />
            <IconButton color="primary" disabled={loading || !input.trim() || configured === false} onClick={() => void send(input)}>
              <SendIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
}
